import { useState } from "react";
import {
  CLOVER_FEE_OVERRIDE_KEYS,
  CLOVER_SECURITY_AND_TRANSARMOR_FIELDS,
  EBT_KEYS,
  FEE_OVERRIDE_FIELDS,
  FLEET_OVERRIDE_FIELDS,
  PIN_DEBIT_KEYS,
  PRICING_TIERS,
  VOYAGER_KEYS,
  WEX_KEYS,
  effectiveAnnualSecurityBundleFee,
  formatFee,
  formatFnsNumber,
  formatPercent,
  selectPricingTier,
  type CloverSecurityAndTransarmor,
  type FeeOverrideField,
  type FleetPricing,
  type PricingTier,
} from "../lib/pricingTiers";
import { stripAmountFormatting } from "../lib/formatters";
import {
  defaultSalesCodeForVolume,
  portfolioPrefixForSalesCode,
} from "../lib/salesCode";
import { icPlusPricingBase } from "../lib/sstshubRateTemplates";

// Currency-shaped fee keys only -- excludes pciProgramCd (a code, handled
// separately below) and the two boolean flags, neither of which formatFee
// can render.
type CurrencyFeeKey = Exclude<
  keyof PricingTier["fees"],
  "pciProgramCd" | "duesAndAssessmentsFlg" | "passthruInterchgCostsFlg"
>;

type FeeOverridesLike = Partial<Record<keyof PricingTier["fees"], string>>;
type FleetOverridesLike = Partial<Record<keyof FleetPricing, string>>;
type CloverSecurityAndTransarmorLike = Partial<
  Record<keyof CloverSecurityAndTransarmor, string>
>;

// The admin's interchange-plus (card percentage) overrides, as raw form
// strings. Percent numbers: "0.15" means 0.15%.
type IcPlusOverridesLike = Partial<
  Record<
    | "visaQualCreditDiscountPct"
    | "mastercardQualCreditDiscountPct"
    | "discoverQualCreditDiscountPct"
    | "amexOptBlueQualDiscountPct",
    string
  >
>;

type ScheduleRow = { label: string; value: string };
type ScheduleGroup = { title: string; rows: ScheduleRow[] };

// The three fees shown in the headline above the disclosure; the full fee
// schedule leaves them out rather than repeating them.
const HEADLINE_FEE_KEYS: FeeOverrideField["key"][] = [
  "transactionFee",
  "statementFee",
  "appFee",
];

// Same per-auth / per-transaction split the merchant's pricing summary
// (PricingConfirmBody, IntakeForm.tsx) uses; every other fee is "monthly and
// miscellaneous".
const AUTH_AND_TRANSACTION_FEE_KEYS: FeeOverrideField["key"][] = [
  "achBatchFee",
  "authFee",
  "voiceAuthFee",
  "addressVerifFee",
  "transactionFee",
];

// An override string (with any "$" / "%" / "," typed for familiarity
// stripped, as buildFeeOverridesInput does) wins when non-blank; otherwise the
// tier default.
function resolveOverride<T>(
  raw: string | undefined,
  tierValue: T,
): string | T {
  const cleaned = stripAmountFormatting(raw ?? "");
  return cleaned !== "" ? cleaned : tierValue;
}

// A fee with no value, or one that resolves to zero, is hidden -- the same
// $0 suppression the pricing summary applies, so the two always agree on
// which fees exist.
function isNonZeroFee(
  value: number | string | null | undefined,
): value is number | string {
  return value != null && value !== "" && Number(value) !== 0;
}

/**
 * Every non-zero fee the merchant will be charged, grouped the way the
 * merchant's pricing summary (PricingConfirmBody, IntakeForm.tsx) groups them,
 * minus the three headline fees. Zero and unset fees are hidden, matching the
 * summary. Exported for tests.
 */
export function buildFeeScheduleGroups(
  tier: PricingTier,
  {
    authFeeOverride,
    feeOverrides,
    fleetOverrides,
    cloverSecurityAndTransarmor,
    annualSecurityBundleFee,
    icPlusPricingOverrides,
    salesCode,
    monthlyVolume,
  }: {
    authFeeOverride?: string;
    feeOverrides?: FeeOverridesLike;
    fleetOverrides?: FleetOverridesLike;
    cloverSecurityAndTransarmor?: CloverSecurityAndTransarmorLike;
    annualSecurityBundleFee?: string;
    icPlusPricingOverrides?: IcPlusOverridesLike;
    // The operator's sales-code pick ("" = Auto) and the monthly volume pick
    // the card-rate template, the same way the backend does.
    salesCode?: string;
    monthlyVolume?: string | number | null;
  },
): ScheduleGroup[] {
  // The two PCI programs are mutually exclusive: ANNUAL bills the annual and
  // non-compliance fees, CONCIERGE bills the monthly concierge fee instead.
  const pciProgramOverride = feeOverrides?.pciProgramCd?.trim();
  const effectivePciProgramCd =
    pciProgramOverride === "CONCIERGE" || pciProgramOverride === "ANNUAL"
      ? pciProgramOverride
      : tier.fees.pciProgramCd;
  const pciHiddenKeys: FeeOverrideField["key"][] =
    effectivePciProgramCd === "ANNUAL"
      ? ["pciConciergeMonthlyFee"]
      : ["pciAnnualFee", "pciNonComplianceFee"];

  const feeRow = (field: FeeOverrideField): ScheduleRow | null => {
    const value =
      field.key === "authFee"
        ? resolveOverride(authFeeOverride, tier.fees.authFee)
        : resolveOverride(
            feeOverrides?.[field.key],
            tier.fees[field.key] as number | string,
          );
    return !isNonZeroFee(value)
      ? null
      : { label: field.label, value: formatFee(value) };
  };
  const isRow = (row: ScheduleRow | null): row is ScheduleRow => row !== null;
  const isCloverFeeKey = (key: FeeOverrideField["key"]) =>
    (CLOVER_FEE_OVERRIDE_KEYS as readonly string[]).includes(key);

  // Dollar fees only: the pass-through flags and the PCI program code aren't
  // fees, and the Clover fees get their own group below.
  const standardFields = FEE_OVERRIDE_FIELDS.filter(
    (f) =>
      (f.kind === "currency" || f.kind === "currencyString") &&
      !HEADLINE_FEE_KEYS.includes(f.key) &&
      !isCloverFeeKey(f.key) &&
      !pciHiddenKeys.includes(f.key),
  );
  const authTransactionRows = standardFields
    .filter((f) => AUTH_AND_TRANSACTION_FEE_KEYS.includes(f.key))
    .map(feeRow)
    .filter(isRow);
  const miscRows = standardFields
    .filter((f) => !AUTH_AND_TRANSACTION_FEE_KEYS.includes(f.key))
    .map(feeRow)
    .filter(isRow);
  // Annual security bundle: our records only (never boarded), but the
  // merchant is billed it, so it's listed with the other recurring fees.
  const securityBundle = effectiveAnnualSecurityBundleFee(
    tier.annualSecurityBundleFee,
    annualSecurityBundleFee ?? "",
  );
  if (securityBundle > 0) {
    miscRows.push({
      label: "Annual security bundle",
      value: formatFee(securityBundle),
    });
  }

  // Clover's platform / security fees (in `fees`) share a group with the
  // Clover Security + TransArmor dollar fees, as on the pricing summary.
  const cloverRows = [
    ...FEE_OVERRIDE_FIELDS.filter((f) => isCloverFeeKey(f.key))
      .map(feeRow)
      .filter(isRow),
    ...CLOVER_SECURITY_AND_TRANSARMOR_FIELDS.filter(
      (f) => f.kind === "currency",
    )
      .map((field): ScheduleRow | null => {
        const value = resolveOverride(
          cloverSecurityAndTransarmor?.[field.key],
          tier.cloverSecurityAndTransarmor[field.key] as number | null,
        );
        return !isNonZeroFee(value)
          ? null
          : { label: field.label, value: formatFee(value) };
      })
      .filter(isRow),
  ];

  // Voyager / WEX / PIN debit / EBT: display-only (never boarded), but billed
  // to the merchant and shown on the pricing summary, so shown here too.
  const fleetRows = (keys: (keyof FleetPricing)[]) =>
    FLEET_OVERRIDE_FIELDS.filter((f) => keys.includes(f.key))
      .map((field): ScheduleRow | null => {
        if (field.kind === "number") {
          // The EBT FNS # is an identifier, not a fee, and has no real tier
          // default; list it only once it has been entered.
          const entered = (fleetOverrides?.[field.key] ?? "").trim();
          return entered
            ? { label: field.label, value: formatFnsNumber(entered) }
            : null;
        }
        const value = resolveOverride(
          fleetOverrides?.[field.key],
          tier.fleetPricing[field.key],
        );
        if (!isNonZeroFee(value)) return null;
        return {
          label: field.label,
          value:
            field.kind === "percent"
              ? formatPercent(value)
              : formatFee(value),
        };
      })
      .filter(isRow);

  // Card percentage pricing: the SSTHUB template base for the volume and the
  // effective sales code's portfolio (the operator's pick, else the volume
  // default -- as the backend resolves it), with any admin override layered on
  // per brand. Same rows and number format as the merchant's pricing summary:
  // Visa / Mastercard / Discover collapse into one row when they match, Amex
  // has its own. A zeroed rate is hidden, like every other zero.
  const base = icPlusPricingBase(
    monthlyVolume,
    portfolioPrefixForSalesCode(salesCode || defaultSalesCodeForVolume(monthlyVolume)),
  );
  const rate = (key: keyof IcPlusOverridesLike, baseValue: number | undefined) => {
    const raw = stripAmountFormatting(icPlusPricingOverrides?.[key] ?? "");
    const value = raw !== "" ? Number(raw) : baseValue;
    return value != null && Number.isFinite(value) ? value : undefined;
  };
  const fmtPct = (v: number) => `${parseFloat(v.toFixed(4))}%`;
  const visa = rate("visaQualCreditDiscountPct", base?.visaQualCreditDiscountPct);
  const mastercard = rate(
    "mastercardQualCreditDiscountPct",
    base?.mastercardQualCreditDiscountPct,
  );
  const discover = rate(
    "discoverQualCreditDiscountPct",
    base?.discoverQualCreditDiscountPct,
  );
  const amex = rate("amexOptBlueQualDiscountPct", base?.amexOptBlueQualDiscountPct);
  const cardRows: ScheduleRow[] = [];
  const pushRate = (label: string, v: number | undefined) => {
    if (v != null && v !== 0) cardRows.push({ label, value: fmtPct(v) });
  };
  if (visa != null && visa === mastercard && mastercard === discover) {
    pushRate("Visa / Mastercard / Discover discount", visa);
  } else {
    pushRate("Visa discount", visa);
    pushRate("Mastercard discount", mastercard);
    pushRate("Discover discount", discover);
  }
  pushRate("Amex discount", amex);

  return [
    { title: "Card percentage pricing", rows: cardRows },
    { title: "Authorization and transaction fees", rows: authTransactionRows },
    { title: "Monthly and miscellaneous fees", rows: miscRows },
    { title: "Clover Security & TransArmor", rows: cloverRows },
    { title: "Voyager", rows: fleetRows(VOYAGER_KEYS) },
    { title: "WEX", rows: fleetRows(WEX_KEYS) },
    { title: "PIN debit", rows: fleetRows(PIN_DEBIT_KEYS) },
    { title: "EBT", rows: fleetRows(EBT_KEYS) },
  ].filter((group) => group.rows.length > 0);
}

// Admin-set override wins over the tier default whenever it's a non-blank
// value; formatFee accepts both, so the raw override string is passed
// through as-is rather than re-parsed. Keeps this in sync with the
// override-resolution used by PricingConfirmBody (IntakeForm.tsx) and
// ManualFeeAdjustmentSection (NewApplication.tsx) so every "what will this
// merchant actually pay" surface agrees.
function effectiveFee(
  tier: PricingTier,
  key: CurrencyFeeKey,
  overrides: FeeOverridesLike | undefined,
): number | string {
  // The override field lets the operator type a "$" for familiarity;
  // stripAmountFormatting removes it so the preview shows a clean amount,
  // matching what buildFeeOverridesInput sends.
  const raw = stripAmountFormatting(overrides?.[key] ?? "");
  return raw !== "" ? raw : tier.fees[key];
}

/**
 * "Based on your volume, you qualify for…" preview that renders below
 * the averageMonthlyVolume input in NewApplication (both the operator-fills
 * form and the send-intake form's optional prefill section, via
 * VolumeFields).
 *
 * Re-renders synchronously on every keystroke — the selection logic is
 * pure and cheap. When the volume is empty or unparseable, renders
 * nothing rather than a "no tier" message; the goal is to *suggest*
 * pricing, not to nag the user about missing input.
 *
 * The overrides are optional: when passed (from NewApplication/
 * EditApplicationModal's admin-only override state), every row shows the
 * EFFECTIVE fee (override if set, else tier default) instead of the raw tier
 * default -- so this preview always matches what the adjustment sections and
 * the merchant's final "this is your pricing" confirmation will actually
 * show. The full fee schedule lists every non-zero fee, including fleet /
 * PIN debit / EBT and Clover (see buildFeeScheduleGroups).
 */
export function PricingTierPreview({
  monthlyVolume,
  authFeeOverride,
  feeOverrides,
  fleetOverrides,
  cloverSecurityAndTransarmor,
  annualSecurityBundleFee,
  icPlusPricingOverrides,
  salesCode,
}: {
  monthlyVolume: string | number | null | undefined;
  authFeeOverride?: string;
  feeOverrides?: FeeOverridesLike;
  fleetOverrides?: FleetOverridesLike;
  cloverSecurityAndTransarmor?: CloverSecurityAndTransarmorLike;
  icPlusPricingOverrides?: IcPlusOverridesLike;
  salesCode?: string;
  // Raw override string from NewApplication/EditApplicationModal's admin
  // state. Blank -> the $199 tier default. Our records only (no CardConnect
  // field).
  annualSecurityBundleFee?: string;
}) {
  const [open, setOpen] = useState(false);
  const tier = selectPricingTier(monthlyVolume);
  if (!tier) return null;

  // Three headline fees that close the sale; the disclosure shows the
  // full schedule for the curious. Picked these three because they're
  // the ones merchants compare across processors.
  const headline = [
    {
      label: "Per-transaction fee",
      value: formatFee(effectiveFee(tier, "transactionFee", feeOverrides)),
    },
    {
      label: "Monthly statement fee",
      value: formatFee(effectiveFee(tier, "statementFee", feeOverrides)),
    },
    {
      label: "App / setup fee",
      value: formatFee(effectiveFee(tier, "appFee", feeOverrides)),
    },
  ];

  const scheduleGroups = buildFeeScheduleGroups(tier, {
    authFeeOverride,
    feeOverrides,
    fleetOverrides,
    cloverSecurityAndTransarmor,
    annualSecurityBundleFee,
    icPlusPricingOverrides,
    salesCode,
    monthlyVolume,
  });

  return (
    <div className="rounded-lg border border-brand-border bg-brand-surface/40 p-3 text-sm">
      <div className="flex items-baseline justify-between gap-2">
        <p className="font-medium text-brand-text">
          You qualify for <span className="text-brand-blue">{tier.label}</span>
        </p>
        {tier.isPlaceholder && (
          <span className="rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-300">
            Preview only
          </span>
        )}
      </div>
      <dl className="mt-2 grid grid-cols-1 gap-1 sm:grid-cols-3">
        {headline.map((row) => (
          <div
            key={row.label}
            className="flex justify-between gap-2 sm:flex-col sm:gap-0"
          >
            <dt className="text-xs text-brand-muted">{row.label}</dt>
            <dd className="font-mono text-brand-text">{row.value}</dd>
          </div>
        ))}
      </dl>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="mt-2 text-xs text-brand-blue underline-offset-2 hover:underline"
        aria-expanded={open}
        aria-controls="pricing-tier-detail"
      >
        {open ? "Hide full fee schedule" : "See full fee schedule"}
      </button>
      {open && (
        <div
          id="pricing-tier-detail"
          className="mt-2 space-y-3 border-t border-brand-border pt-2 text-xs"
        >
          {scheduleGroups.length === 0 ? (
            <p className="text-brand-muted">No other fees apply.</p>
          ) : (
            scheduleGroups.map((group) => (
              <div key={group.title}>
                <p className="mb-1 font-medium text-brand-text">
                  {group.title}
                </p>
                <dl className="grid grid-cols-2 gap-x-3 gap-y-1">
                  {group.rows.map((row) => (
                    <FeeRow
                      key={row.label}
                      label={row.label}
                      value={row.value}
                    />
                  ))}
                </dl>
              </div>
            ))
          )}
        </div>
      )}
      {tier.isPlaceholder && (
        <p className="mt-2 text-[11px] italic text-brand-muted">
          Placeholder pricing for layout review. Real Payment Products tiers
          will replace these values before launch.
        </p>
      )}
    </div>
  );
}

// `value` is already formatted (a dollar amount, a percent, or the EBT FNS #).
function FeeRow({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt className="text-brand-muted">{label}</dt>
      <dd className="text-right font-mono text-brand-text">{value}</dd>
    </>
  );
}

/**
 * Export the tier list so a future "compare all tiers" UI can render the
 * full ladder. Keeping a single import surface (`./PricingTierPreview`)
 * is convenient for the operator side.
 */
export { PRICING_TIERS };
